import { useState, useEffect } from 'react';
import { useSitemap, usePrefs } from 'wsm-common-data';
import logger from 'ws-scripts/modules/logger';
import { useLocationsDataReady } from './useLocationsDataReady';
import { buildNearestLocationTiles } from '../utils/buildNearestLocationTiles';

const LOG_PREFIX = '[ws-locations][useLocationsData]';
const DEFAULT_MAX_TILE_COUNT = 3;

/**
 * Hook to integrate with ws-locations-data widget.
 * Uses the global window.DDC.LocationsData API to:
 * 1. Read LocationsData from ws-locations-data databus.
 * 2. Transform raw LocationsData results into tile-ready data via
 *    `buildNearestLocationTiles`, deriving the location-detail URL prefix
 *    from the sitemap (`LOCATION_DETAILS` entry, with the trailing
 *    `.htm` extension stripped) and trimming the result to the
 *    `maxTileCount` pref.
 */
export const useLocationsData = () => {
	const [locations, setLocations] = useState([]);
	const [error, setError] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const sitemap = useSitemap();
	const { showAddress, maxTileCount } = usePrefs();
	const rawLocationDetailPageUrl = sitemap.get('LOCATION_DETAILS');
	const locationDetailPagePart = (rawLocationDetailPageUrl || '').slice(
		0,
		-4
	);
	// maxTileCount is an externally-editable text pref, so it arrives as a
	// string and can be blank. Anything that is not a positive
	// number falls back to the default rather than hiding every tile.
	const parsedTileCount = Number(maxTileCount);
	const tileCountLimit =
		Number.isFinite(parsedTileCount) && parsedTileCount > 0
			? parsedTileCount
			: DEFAULT_MAX_TILE_COUNT;

	// hook to check if LocationsData is ready
	const { isReady, error: readyError } = useLocationsDataReady();

	// Set error if LocationsData failed to initialize
	useEffect(() => {
		if (readyError) {
			setError(readyError);
			setIsLoading(false);
		}
	}, [readyError]);

	// Read locations data
	useEffect(() => {
		if (!isReady) {
			logger.debug(`${LOG_PREFIX} Not subscribing - isReady: ${isReady}`);
			return () => {};
		}

		const locationsData = window.DDC.LocationsData;
		const handleLocationsUpdate = () => {
			logger.debug(`${LOG_PREFIX} Received locations update`);

			const rawLocations = locationsData.getNearestLocations();
			const newLocations = buildNearestLocationTiles(
				rawLocations,
				locationDetailPagePart,
				showAddress === 'true',
				tileCountLimit
			);
			const newError = locationsData.getError();

			setLocations(newLocations);
			setError(newError);
			setIsLoading(false);

			logger.debug(
				`${LOG_PREFIX} Updated state - count: ${
					newLocations.length
				}, hasError: ${!!newError}`
			);
		};

		handleLocationsUpdate();

		const unsubscribe = locationsData.registerNearestLocationsNotification(
			handleLocationsUpdate
		);
		// Cleanup subscriptions on unmount
		return () => {
			logger.debug(`${LOG_PREFIX} Unsubscribing from notifications`);
			if (unsubscribe) {
				unsubscribe();
			}
		};
	}, [isReady, locationDetailPagePart, showAddress, tileCountLimit]);

	return {
		locations,
		error,
		isLoading
	};
};
